import { Message, Chat, ChatParticipant } from '../types';

/**
 * Get display name for a chat
 */
export const getChatDisplayName = (
  chat: Chat,
  currentUserId: string
): string => {
  // For direct chats, ALWAYS show other user's name (even if chat has explicit name)
  if (chat.type === 'direct' && chat.participants && chat.participants.length > 0) {
    // Find the other participant (not the current user)
    const otherParticipant = chat.participants.find(
      (p) => p.user_id !== currentUserId
    );
    
    // If we found another participant with user data
    if (otherParticipant?.user) {
      const displayName = otherParticipant.user.nickname || otherParticipant.user.full_name;
      if (displayName) return displayName;
    }
    
    return 'Unknown User';
  }

  // If chat has explicit name (for group/team chats), use it
  if (chat.name) return chat.name;

  // For group chats, show participant names if no name set
  if (chat.type === 'group' && chat.participants && chat.participants.length > 0) {
    const participantNames = chat.participants
      .filter((p) => p.user_id !== currentUserId)
      .map((p) => p.user?.nickname || p.user?.full_name)
      .filter(Boolean)
      .slice(0, 3);
    
    if (participantNames.length > 0) {
      return participantNames.join(', ');
    }
    
    // Fallback for group chats
    const allNames = chat.participants
      .map((p) => p.user?.nickname || p.user?.full_name)
      .filter(Boolean)
      .slice(0, 3);
    
    if (allNames.length > 0) {
      return allNames.join(', ');
    }
  }

  // For team chats, use team name
  if (chat.type === 'team') {
    return chat.name || 'Team Chat';
  }

  // Default
  return 'Chat';
};

/**
 * Get avatar URL for a chat
 */
export const getChatAvatar = (
  chat: Chat,
  currentUserId: string
): string | undefined => {
  // If chat has explicit avatar, use it
  if (chat.avatar_url) return chat.avatar_url;

  // For direct chats, use other user's avatar
  if (chat.type === 'direct' && chat.participants) {
    const otherUser = chat.participants.find(
      (p) => p.user_id !== currentUserId
    );
    return otherUser?.user?.avatar_url;
  }

  return undefined;
};

/**
 * Format message content for preview
 */
export const formatMessagePreview = (message: Message): string => {
  if (message.is_deleted) return 'Message deleted';

  switch (message.type) {
    case 'text':
      return message.content || '';
    case 'image':
      return '📷 Photo';
    case 'video':
      return '🎥 Video';
    case 'file':
      return `📎 ${message.file_name || 'File'}`;
    case 'audio':
      return '🎵 Audio';
    case 'system':
      return message.content || 'System message';
    default:
      return 'Message';
  }
};

/**
 * Group messages by date
 */
export const groupMessagesByDate = (messages: Message[]): {
  date: string;
  messages: Message[];
}[] => {
  const groups: { [key: string]: Message[] } = {};

  messages.forEach((message) => {
    const date = new Date(message.created_at);
    const dateKey = date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
  });

  return Object.entries(groups).map(([date, msgs]) => ({
    date,
    messages: msgs,
  }));
};

/**
 * Format timestamp for message
 */
export const formatMessageTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Calculate unread count for a chat
 */
export const calculateUnreadCount = (
  messages: Message[],
  lastReadAt?: string
): number => {
  if (!lastReadAt) return messages.length;

  const lastReadDate = new Date(lastReadAt);
  return messages.filter(
    (m) => new Date(m.created_at) > lastReadDate
  ).length;
};

/**
 * Parse mentions from message content
 */
export const parseMentions = (
  content: string,
  participants: ChatParticipant[]
): string[] => {
  const mentionRegex = /@(\w+)/g;
  const mentions: string[] = [];
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    const username = match[1].toLowerCase();
    
    // Find user by nickname or name
    const user = participants.find(
      (p) =>
        p.user?.nickname?.toLowerCase() === username ||
        p.user?.full_name?.toLowerCase().includes(username)
    );

    if (user && !mentions.includes(user.user_id)) {
      mentions.push(user.user_id);
    }
  }

  return mentions;
};

/**
 * Highlight mentions in message content
 */
export const highlightMentions = (
  content: string,
  currentUserId: string,
  mentionedUserIds?: string[]
): string => {
  if (!mentionedUserIds?.includes(currentUserId)) return content;

  // Simple highlight - can be enhanced in UI
  return content.replace(/@(\w+)/g, '**@$1**');
};

/**
 * Get chat style based on type and weekend status
 */
export const getChatStyle = (
  chat: Chat,
  activeWeekendId?: string
): {
  borderColor: string;
  badge?: string;
  opacity: number;
} => {
  if (chat.type === 'team') {
    const isCurrentWeekend = chat.weekend_id === activeWeekendId;
    return {
      borderColor: isCurrentWeekend ? '#10B981' : '#F59E0B', // Green or amber
      badge: isCurrentWeekend ? undefined : 'Previous Weekend',
      opacity: isCurrentWeekend ? 1 : 0.7,
    };
  }

  return {
    borderColor: '#7C3AED', // Purple default
    opacity: 1,
  };
};

/**
 * Get file type icon
 */
export const getFileTypeIcon = (fileType?: string): string => {
  if (!fileType) return '📎';
  
  if (fileType.startsWith('image/')) return '📷';
  if (fileType.startsWith('video/')) return '🎥';
  if (fileType.startsWith('audio/')) return '🎵';
  if (fileType.includes('pdf')) return '📄';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
  if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📈';
  
  return '📎';
};

/**
 * Format file size
 */
export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * Check if user is typing (within last 3 seconds)
 */
export const isUserTyping = (updatedAt: string): boolean => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const diffSeconds = (now.getTime() - updated.getTime()) / 1000;
  return diffSeconds < 3;
};

